import { TUseOptionsParams } from '@/shared/components/ui/form/components/searchable-select/hooks/use-options/use-options.types';

const SELECTED = 'selected';
const UNSELECTED = 'unselected';

export const useOptions = <TOptionShape>({
  value,
  options,
  getItemId,
  disableSort,
}: TUseOptionsParams<TOptionShape>) => {
  const isSelected = (val: TOptionShape) => {
    if (Array.isArray(value)) {
      return value.some((v: TOptionShape) => getItemId(v) === getItemId(val));
    }

    if (typeof value === 'string' && typeof val === 'string') {
      return val === value;
    }

    return getItemId(val) === getItemId(value);
  };

  const groupedOptions = () => {
    const optionsWithGroups = options?.map((option) => ({
      ...option,
      group: isSelected(option.value) ? SELECTED : UNSELECTED,
    }));

    if (!optionsWithGroups) {
      return [];
    }

    return [...optionsWithGroups].sort((a, b) => {
      // First sort by group (selected first) - HIGHEST PRIORITY
      if (a.group !== b.group) {
        return a.group === SELECTED ? -1 : 1;
      }

      // Within the same group, handle disabled items (they go to the end of the group)
      if (a.disabled && !b.disabled) return 1;
      if (!a.disabled && b.disabled) return -1;

      // Within the same group and enabled/disabled status, check if options end with wildcard (they go to the top)
      if (typeof a.value === 'string' && typeof b.value === 'string') {
        const aIsWildcard = a.value?.endsWith(':*') || false;
        const bIsWildcard = b.value?.endsWith(':*') || false;

        if (aIsWildcard && !bIsWildcard) return -1;
        if (!aIsWildcard && bIsWildcard) return 1;
      }

      if (disableSort) {
        return 0;
      }

      // Finally, sort alphabetically by label within the same group
      const labelA = a.label?.toLowerCase() || '';
      const labelB = b.label?.toLowerCase() || '';
      return labelA.localeCompare(labelB);
    });
  };

  return {
    groupedOptions: groupedOptions(),
    isSelected,
  };
};
